/**
 * Localized zod validation messages that NAME the field and its unit.
 *
 * ## Why this exists (TASK-4747)
 *
 * Two separate defects were shipping together in a fully-localized RU app:
 *
 * 1. **Hardcoded English literals.** ~15 schemas carried messages like
 *    `"Must be between 1 and 60"` — English in a Russian UI, and worse, naming
 *    neither the field nor the unit. Three different `PlanningTab` inputs
 *    (`solver_timeout_sec`, `reoptimize_timeout_sec`, `traffic_factor`) produced
 *    three interchangeable "Must be between X and Y" strings; a dispatcher who
 *    scrolled past the highlighted input could not tell which one it belonged to.
 *
 * 2. **zod's own English defaults.** A presence or type failure that supplies no
 *    `required_error` / `invalid_type_error` falls through to zod's built-ins —
 *    *"Required"*, *"Expected number, received nan"*. Those are developer strings
 *    and they are untranslatable, because they never pass through i18next at all.
 *
 * ## The contract
 *
 * Every helper takes the translator plus a **localized field label** (pass the
 * `t()` of the same key the `<label>` renders, so message and label always agree
 * — including its unit, e.g. "Average Speed (km/h)"). The message therefore names
 * the field even when it is read out of context, e.g. by a screen reader or in a
 * form-level error summary.
 *
 * These are plain functions rather than a zod `errorMap` on purpose: a global
 * error map has no access to a human field label, only to the schema path
 * (`traffic_factor`), which would produce a message naming the API field name
 * instead of the visible label.
 *
 * ## i18n gate blind spot — read before changing the keys
 *
 * `npm run check:i18n` only scans STATIC string-literal keys. The keys below are
 * passed as literals inside these helpers, so they ARE covered. If a future
 * change composes a key dynamically (a template literal built from a variable)
 * the gate goes blind to it and rendered-text vitest assertions become its only
 * protection. Keep the literals literal.
 *
 * (The scanner is not comment-aware, so this paragraph deliberately avoids
 * writing a call-shaped example — one here would be reported as a missing key.)
 */

import type { TranslateFn } from "./apiErrors";

/**
 * "«Average Speed (km/h)» is required." — replaces zod's bare English
 * *"Required"*, which is what an empty input produces when no `required_error`
 * is supplied.
 */
export function requiredMessage(t: TranslateFn, field: string): string {
  return t("validation.required", { field });
}

/**
 * "«Average Speed (km/h)» must be a number." — replaces zod's
 * *"Expected number, received nan"*, which is what an `input type="number"`
 * produces the moment it is cleared or contains anything non-numeric.
 */
export function mustBeNumberMessage(t: TranslateFn, field: string): string {
  return t("validation.mustBeNumber", { field });
}

/**
 * "«Route Build Timeout (sec)» must be between 1 and 60." — the message the
 * three `PlanningTab` range checks used to render as a bare, unattributable
 * "Must be between 1 and 60".
 */
export function betweenMessage(
  t: TranslateFn,
  field: string,
  min: number | string,
  max: number | string,
): string {
  return t("validation.between", { field, min, max });
}

/** "«Priority» must be at least 1." */
export function minMessage(
  t: TranslateFn,
  field: string,
  min: number | string,
): string {
  return t("validation.min", { field, min });
}

/** "«Priority» must be at most 99." */
export function maxMessage(
  t: TranslateFn,
  field: string,
  max: number | string,
): string {
  return t("validation.max", { field, max });
}

/** "«Email» must be a valid email address." */
export function invalidEmailMessage(t: TranslateFn, field: string): string {
  return t("validation.invalidEmail", { field });
}

/** "«Base URL» must be a valid URL." */
export function invalidUrlMessage(t: TranslateFn, field: string): string {
  return t("validation.invalidUrl", { field });
}

/** "«Route Build Timeout (sec)» must be a whole number." */
export function mustBeIntegerMessage(t: TranslateFn, field: string): string {
  return t("validation.mustBeInteger", { field });
}

/**
 * "«Delivery Date» must be in YYYY-MM-DD format."
 *
 * The format token is deliberately NOT translated — `YYYY-MM-DD` is the literal
 * pattern the input accepts, so localizing it (e.g. to ГГГГ-ММ-ДД) would tell a
 * Russian user to type characters the field rejects.
 */
export function dateFormatMessage(t: TranslateFn, field: string): string {
  return t("validation.dateFormat", { field, format: "YYYY-MM-DD" });
}

/**
 * "«Shift start» must be in HH:MM format."
 *
 * As with {@link dateFormatMessage}, the `HH:MM` token is the literal pattern the input
 * accepts and is deliberately left untranslated.
 */
export function timeFormatMessage(t: TranslateFn, field: string): string {
  return t("validation.timeFormat", { field, format: "HH:MM" });
}

/** "«Name» must be at most 255 characters." */
export function maxLengthMessage(
  t: TranslateFn,
  field: string,
  max: number,
): string {
  return t("validation.maxLength", { field, count: max });
}

/** "«Weight (kg)» cannot be negative." */
export function nonNegativeMessage(t: TranslateFn, field: string): string {
  return t("validation.nonNegative", { field });
}

/**
 * The `{ required_error, invalid_type_error }` pair, both localized, ready to
 * spread into `z.number({...})` / `z.enum([...], {...})`.
 *
 * Supplied as one object because the two must never diverge: a field that
 * localizes only `invalid_type_error` still leaks zod's English *"Required"* the
 * moment the input is empty rather than malformed, and that asymmetry is exactly
 * what shipped before this task.
 */
export function zodFieldErrors(
  t: TranslateFn,
  field: string,
): { required_error: string; invalid_type_error: string } {
  return {
    required_error: requiredMessage(t, field),
    invalid_type_error: mustBeNumberMessage(t, field),
  };
}
